/**
 * Escape a field for CSV (RFC-style: double quotes doubled inside quoted fields).
 */
export function escapeCsvField(value: string): string {
  const needsQuotes = /[",\r\n]/.test(value)
  const escaped = value.replace(/"/g, '""')
  return needsQuotes ? `"${escaped}"` : escaped
}

// Safe value handlers - ensure values are never undefined, null, or negative
export function safeNumber(value: number | null | undefined): number {
  if (value === null || value === undefined || isNaN(value) || value < 0) {
    return 0
  }
  return value
}

export function safePercentage(value: number | null | undefined): number {
  const num = safeNumber(value)
  // Clamp percentage between 0 and 100
  return Math.min(Math.max(num, 0), 100)
}

// Format large durations in a more relatable way (for total durations)
export function formatLargeDuration(seconds: number | null | undefined): {
  value: string
  label: string
  context?: string
} {
  const safeSeconds = safeNumber(seconds)
  const hours = Math.floor(safeSeconds / 3600)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)

  // If more than a month, show in months
  if (months >= 1) {
    const remainingDays = days % 30
    const workDays = Math.floor(days / 5) // Approximate work days (5 days per week)
    return {
      value: `${months}`,
      label: `month${months > 1 ? 's' : ''}${remainingDays > 0 ? ` ${remainingDays}d` : ''}`,
      context: workDays > 0 ? `~${workDays}d` : undefined,
    }
  }

  // If more than a week, show in weeks
  if (weeks >= 1) {
    const remainingDays = days % 7
    return {
      value: `${weeks}`,
      label: `week${weeks > 1 ? 's' : ''}${remainingDays > 0 ? ` ${remainingDays}d` : ''}`,
    }
  }

  // If more than a day, show in days
  if (days >= 1) {
    const remainingHours = hours % 24
    return {
      value: `${days}`,
      label: `day${days > 1 ? 's' : ''}${remainingHours > 0 ? ` ${remainingHours}h` : ''}`,
    }
  }

  // Otherwise show in hours
  if (hours >= 1) {
    const remainingMinutes = Math.floor((safeSeconds % 3600) / 60)
    return {
      value: `${hours}`,
      label: `hour${hours > 1 ? 's' : ''}${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`,
    }
  }

  // Less than an hour
  const minutes = Math.floor(safeSeconds / 60)
  return {
    value: `${minutes}`,
    label: `minute${minutes !== 1 ? 's' : ''}`,
  }
}

// Format duration for call duration (should be minutes/seconds, not hours)
// If duration seems unrealistic (> 2 hours), show hours instead
export function formatCallDuration(seconds: number | null | undefined): string {
  const safeSeconds = safeNumber(seconds)
  const minutes = Math.floor(safeSeconds / 60)
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const remainingSeconds = Math.round(safeSeconds % 60)

  // If more than 2 hours, something is likely wrong with the data
  // But display it in hours for clarity
  if (hours >= 1) {
    if (remainingMinutes === 0) {
      return `${hours}h`
    }
    return `${hours}h ${remainingMinutes}m`
  }

  // Normal call duration (under 1 hour)
  if (minutes === 0) {
    return `${remainingSeconds}s`
  }
  if (remainingSeconds === 0) {
    return `${minutes}m`
  }
  return `${minutes}m ${remainingSeconds}s`
}

// Format duration as structured parts for styled display
export function formatCallDurationParts(
  seconds: number | null | undefined,
): { value: string; label: string }[] {
  const safeSeconds = safeNumber(seconds)
  const minutes = Math.floor(safeSeconds / 60)
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const remainingSeconds = Math.round(safeSeconds % 60)

  const parts: { value: string; label: string }[] = []

  if (hours >= 1) {
    parts.push({ value: String(hours), label: 'h' })
    if (remainingMinutes > 0) {
      parts.push({ value: String(remainingMinutes), label: 'm' })
    }
  } else if (minutes > 0) {
    parts.push({ value: String(minutes), label: 'm' })
    if (remainingSeconds > 0) {
      parts.push({ value: String(remainingSeconds), label: 's' })
    }
  } else {
    parts.push({ value: String(remainingSeconds), label: 's' })
  }

  return parts
}

// Format time saved in hours (with 1.1k abbreviation for >= 1000 hours)
export function formatTimeSaved(seconds: number | null | undefined): {
  value: string
  label: string
} {
  const safeSeconds = safeNumber(seconds)
  const hours = Math.floor(safeSeconds / 3600)
  const remainingMinutes = Math.floor((safeSeconds % 3600) / 60)

  if (hours >= 1000) {
    const abbreviated = (hours / 1000).toFixed(1)
    return {
      value: `${abbreviated}k`,
      label: 'h',
    }
  }

  if (hours >= 1) {
    return {
      value: `${hours}`,
      label: `h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`,
    }
  }

  return {
    value: `${remainingMinutes}`,
    label: `minute${remainingMinutes !== 1 ? 's' : ''}`,
  }
}

// Format time saved as structured parts for styled display
export function formatTimeSavedParts(seconds: number | null | undefined): {
  parts: { value: string; label: string }[]
} {
  const safeSeconds = safeNumber(seconds)
  const totalHours = Math.floor(safeSeconds / 3600)
  const totalMinutes = Math.floor((safeSeconds % 3600) / 60)

  const parts: { value: string; label: string }[] = []

  if (totalHours >= 1000) {
    const abbreviated = (totalHours / 1000).toFixed(1)
    parts.push({ value: `${abbreviated}k`, label: 'h' })
  } else if (totalHours > 0) {
    parts.push({ value: String(totalHours), label: 'h' })
    if (totalMinutes > 0) {
      parts.push({ value: String(totalMinutes), label: 'm' })
    }
  } else {
    parts.push({ value: String(totalMinutes), label: 'm' })
  }

  return { parts }
}

export function formatNumber(num: number | null | undefined): string {
  const safeNum = safeNumber(num)
  return new Intl.NumberFormat('en-US').format(safeNum)
}

export function formatPercentage(
  value: number | null | undefined,
  decimals: number = 1,
): string {
  const safeValue = safePercentage(value)
  return `${safeValue.toFixed(decimals)}%`
}

function parseReportingDateInput(dateInput: string): Date {
  // Date-only values from <input type="date"> should be interpreted as calendar
  // dates, not UTC midnights, to avoid off-by-one display in non-UTC timezones.
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
    const [year, month, day] = dateInput.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
  return new Date(dateInput)
}

export function formatReportingDateInput(
  dateInput: string,
  options: Intl.DateTimeFormatOptions,
): string {
  return parseReportingDateInput(dateInput).toLocaleDateString('en-US', options)
}

// Format date range for display in tooltips
export function formatDateRangeForDisplay(
  dateRange: string,
  appliedCustomDates?: { start: string; end: string } | null,
): string {
  if (dateRange === 'custom' && appliedCustomDates) {
    const startDate = formatReportingDateInput(appliedCustomDates.start, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    const endDate = formatReportingDateInput(appliedCustomDates.end, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
    return `${startDate} - ${endDate}`
  }

  switch (dateRange) {
    case '7d':
      return 'the last 7 days'
    case '30d':
      return 'the last 30 days'
    case '90d':
      return 'the last 90 days'
    default:
      return dateRange
  }
}
